import { useRef, useState } from 'react';
import { t } from '@lingui/macro';

const isComplete = ({ path, value }) => path.trim() && value.trim();

// The API drops a leading `data.`, so `data.vin` and `vin` are the same field.
const fieldOf = (path) => path.trim().replace(/^data\./i, '');

// The query string would nest the brackets and the API reject the whole filter.
const isArrayPath = (path) => /[[\]]/.test(path);

// The filter maps each field to one value, so a field can fill only one row.
const duplicateFields = (rows) => {
  const fields = rows.map(({ path }) => fieldOf(path)).filter(Boolean);
  return new Set(
    fields.filter((field, index) => fields.indexOf(field) !== index),
  );
};

const toParameters = (rows) =>
  Object.fromEntries(
    rows.map(({ path, value }) => [path.trim(), value.trim()]),
  );

export default function useParameterRows(onSearch) {
  const [rows, setRows] = useState([]);
  const [appliedRows, setAppliedRows] = useState([]);
  const nextId = useRef(0);

  const duplicates = duplicateFields(rows);
  const completeRows = rows.filter(isComplete);
  const canApply =
    duplicateFields(completeRows).size === 0 &&
    !completeRows.some(({ path }) => isArrayPath(path));

  const search = (next) => {
    setAppliedRows(next);
    onSearch(toParameters(next));
  };

  const warningFor = (path) => {
    if (isArrayPath(path)) return t`Array fields can't be searched`;
    if (duplicates.has(fieldOf(path))) return t`Already used in another row`;
    return undefined;
  };

  const add = () => {
    nextId.current += 1;
    const id = nextId.current;
    setRows((current) => [...current, { id, path: '', value: '' }]);
  };

  const update = (id, changes) =>
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...changes } : row)),
    );

  const remove = (id) => {
    setRows((current) => current.filter((row) => row.id !== id));
    if (appliedRows.some((row) => row.id === id)) {
      search(appliedRows.filter((row) => row.id !== id));
    }
  };

  const apply = () => {
    if (canApply) search(completeRows);
  };

  const reset = () => {
    setRows([]);
    setAppliedRows([]);
  };

  return { rows, canApply, warningFor, add, update, remove, apply, reset };
}
